import React, { useState } from "react";
import moment from "moment";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendar, faEdit, faTrash, faQuestionCircle, faShare } from "@fortawesome/free-solid-svg-icons";
import { useToasts } from "react-toast-notifications";
import Button from "components/CustomButton";
import { SURVEY_STATUS } from "store/reducers/surveyReducer";
import DeleteSurveyModal from "../Manage/DeleteSurveyModal";
import ShareSurvey from "../Manage/ShareSurvey";
import EditSurvey from "./EditSurvey";
import UserPlaceholder from "assets/img/user.png";

const SurveyCard = ({ survey, dispatch, reload, teamSurveys, activeSurvey, setActiveSurvey }) => {
  const { addToast } = useToasts();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Determine survey status
  const getStatusClass = (status) => {
    if (status === SURVEY_STATUS.ACTIVE) {
      return "success"; // Published/Active
    }
    if (status === SURVEY_STATUS.DRAFT) {
      return "secondary"; // Draft
    }
    return "danger"; // Inactive
  };

  const getStatusText = (status) => {
    if (status === SURVEY_STATUS.ACTIVE) {
      return "Published";
    }
    if (status === SURVEY_STATUS.DRAFT) {
      return "Draft";
    }
    return "Closed";
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleManage = () => {
    setActiveSurvey(survey.id);
  };

  const questionsCount = survey.questions ? survey.questions.length : 0;
  const responsesCount = survey.answered_users || 0;
  const totalUsers = survey.total_users || 0;

  return (
    <>
      <div className="col-12 col-sm-6 col-md-4 col-lg-6 col-xl-6 mb-4">
        <div className={`card h-100 d-flex flex-column survey-list-card ${activeSurvey === survey.id ? 'active-survey-card' : ''}`}>
          <div className="card-body flex-grow-1 d-flex flex-column">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <h5 className="card-title mb-1 text-truncate flex-grow-1 me-2">
                <strong>{survey.title}</strong>
              </h5>
              <span className={`survey-status-pill badge bg-${getStatusClass(survey.status)}`}>
                {getStatusText(survey.status)}
              </span>
            </div>
            
            <div className="d-flex align-items-center mb-2 gap-3 flex-wrap">
              <span className="d-flex align-items-center">
                <FontAwesomeIcon icon={faQuestionCircle} className="me-1 text-primary" />
                <small className="text-muted">
                  {questionsCount} {questionsCount === 1 ? 'question' : 'questions'}
                </small>
              </span>
              <span className="d-flex align-items-center">
                <FontAwesomeIcon icon={faCalendar} className="me-1 text-secondary" />
                <small className="text-muted">
                  {survey.created_at ? moment(survey.created_at).format("MMM DD, YYYY") : 
                   survey.createdAt ? moment(survey.createdAt).format("MMM DD, YYYY") : 
                   survey.date ? moment(survey.date).format("MMM DD, YYYY") : ''}
                </small>
              </span>
            </div>

            {survey.status === SURVEY_STATUS.INACTIVE && responsesCount > 0 && (
              <div className="mb-2">
                <small className="text-muted">
                  Responses: <span className="fw-bold text-primary">{responsesCount}/{totalUsers}</span>
                </small>
              </div>
            )}

            {survey.original_survey && (
              <div className="mb-2">
                <div className="d-flex align-items-center gap-1" style={{ fontStyle: "italic" }}>
                  <img
                    height={20}
                    width={20}
                    src={survey.original_survey.user?.avatar ?? UserPlaceholder}
                    alt=""
                    style={{ borderRadius: "50%" }}
                  />
                  <small className="text-muted">
                    Shared by {survey.original_survey.user?.full_name || "Unknown"} from {survey.original_survey.team?.name}
                  </small>
                </div>
              </div>
            )}

            <div className="d-flex align-items-center mt-auto gap-2 flex-wrap">
              <Button 
                buttonClass="btn btn-link p-0 manage-survey-link" 
                onClick={handleManage}
              >
                Manage Survey &rarr;
              </Button>
              {!survey.is_shared && (
                <>
                  <Button 
                    buttonClass="icon-btn-edit ms-auto" 
                    onClick={handleEdit} 
                    title="Edit Survey"
                    disabled={survey.status === SURVEY_STATUS.INACTIVE}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </Button>
                  <Button 
                    buttonClass="icon-btn-share" 
                    onClick={() => setShowShareModal(true)} 
                    title="Share Survey"
                    disabled={
                      survey.status === SURVEY_STATUS.INACTIVE || 
                      survey.status === SURVEY_STATUS.DRAFT ||
                      (survey.status === SURVEY_STATUS.ACTIVE && questionsCount === 0)
                    }
                  >
                    <FontAwesomeIcon icon={faShare} />
                  </Button>
                  <Button 
                    buttonClass="icon-btn-delete" 
                    onClick={handleDelete} 
                    title="Delete Survey"
                    disabled={survey.status === SURVEY_STATUS.INACTIVE}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Survey Modal */}
      {showEditModal && (
        <EditSurvey
          survey={survey}
          onClose={(updated) => {
            setShowEditModal(false);
            if (updated) {
              dispatch({ type: "setReload", value: !reload });
            }
          }}
          dispatch={dispatch}
          reload={reload}
          teamSurveys={teamSurveys}
        />
      )}

      {/* Delete Survey Modal */}
      {showDeleteModal && (
        <DeleteSurveyModal
          surveyId={survey.id}
          onClose={(flag) => {
            if (flag === true) {
              dispatch({
                type: "setTeamSurveys",
                value: teamSurveys.filter((s) => s.id !== survey.id),
              });
              addToast("Survey deleted successfully.", {
                appearance: "success",
              });
            }
            setShowDeleteModal(false);
          }}
        />
      )}

      {/* Share Survey Modal */}
      {showShareModal && (
        <ShareSurvey
          surveyId={survey.sharing_id || survey.id}
          assignedTeams={survey.assigned_teams || []}
          dispatch={dispatch}
          reload={reload}
          onClose={() => {
            setShowShareModal(false);
          }}
        />
      )}
    </>
  );
};

export default SurveyCard;